import { CircleInfoIcon, EyeIcon } from "@xola/icons";
import { flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, requiredString } from "common";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    CommunicationChannelType,
    EmailMessageTemplate,
    EmailTemplate,
    MessageTemplate,
    SmsMessageTemplate,
} from "types";
import { object } from "yup";
import { RichEditor } from "../../Common/RichEditor";
import { EmailVariables, variableItems, Variables } from "../../Common/RichEditor/Variable.type";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { PreviewMessageTemplateDrawer } from "./PreviewMessageTemplateDrawer";
import { api } from "@/api/api";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";

const validationSchema = object().shape({
    name: requiredString(),
});

interface MessageTemplateFormProps {
    template?: MessageTemplate;
}

const initialEmailValues: EmailMessageTemplate = {
    subject: "",
    body: "",
};

const initialSmsValues: SmsMessageTemplate = {
    body: "",
};

export const MessageTemplateForm = ({ template }: MessageTemplateFormProps) => {
    const [visible, { toggle }] = useVisibility(false);
    const [previewType, setPreviewType] = useState<CommunicationChannelType>();

    // We need this state for holding the rich editor values
    // Since we can't use the `value/onChange` pattern on the editor
    // Otherwise we get an infinite loop
    const [emailBody, setEmailBody] = useState(template?.email?.body ?? "");
    const [smsBody, setSmsBody] = useState(template?.sms?.body ?? "");
    const [templateId, setTemplateId] = useState(template?.id);

    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();

    useEffect(() => {
        if (template) {
            setEmailBody(template.email?.body ?? "");
            setSmsBody(template.sms?.body ?? "");
            setTemplateId(template.id);
        }
    }, [template]);

    const { mutateAsync: saveTemplate, isLoading: isSaving } = useMutation(async (values: MessageTemplate) => {
        await (values.id ? api.put(`/messageTemplates/${values.id}`, values) : api.post("/messageTemplates", values));
        flash.success("Successfully saved message template");
        invalidate("messageTemplates", values.id);
        history.push("/customers/messageTemplates");
    });

    const {
        mutateAsync: previewTemplate,
        isLoading: isLoadingPreview,
        data: templatePreview,
    } = useMutation(
        async (values: EmailMessageTemplate | SmsMessageTemplate) => {
            const isEmail = "subject" in values;
            const { data } = await api.post<string>(`/sellers/${seller.id}/messages/preview`, {
                template: isEmail
                    ? EmailTemplate.TEMPLATE_COMMUNICATION_EMAIL
                    : EmailTemplate.TEMPLATE_COMMUNICATION_SMS,
                messageTemplate: { channels: [{ type: isEmail ? "email" : "sms", ...values }] },
            });

            return data;
        },
        { onSuccess: () => toggle() },
    );

    const handleSubmit = (values: MessageTemplate) => {
        saveTemplate(values);
    };

    const handleResetClick = (reset: () => void) => () => {
        // Reset form
        reset();

        // This is how we can force the rich editor to reset
        // 1. Empty the state for email and sms body
        setEmailBody(" ");
        setSmsBody(" ");

        setTimeout(() => {
            // 2. Reset the state for email and sms body back to original values
            setEmailBody(template?.email?.body ?? "");
            setSmsBody(template?.sms?.body ?? "");
        }, 0);

        history.push("/customers/messageTemplates");
    };

    const enabledSellerPreferenceSms = seller.preferences?.communicationChannels?.enabledChannels.includes("sms");

    const initialValues: MessageTemplate = {
        name: "",
        seller: { id: seller.id },
    };

    return (
        <>
            <XolaFormik
                enableReinitialize
                initialValues={template ? templateToInitialValues(template) : initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, setFieldValue, values, handleReset }) => (
                    <ContentLayout className="mb-16 flex flex-1 flex-col space-y-6">
                        <fieldset
                            disabled={isSaving || isLoadingPreview}
                            className="mb-16 flex flex-1 flex-col space-y-6"
                        >
                            <h1 className="font-bold">Template</h1>
                            <h4 className="font-bold">Name</h4>

                            <FormField name="name" label="Template Name">
                                <InputField isRequired />
                            </FormField>

                            <Switch.Group>
                                <Switch
                                    isChecked={!!values.email}
                                    size="large"
                                    onChange={(enabled: boolean) =>
                                        setFieldValue("email", enabled ? template?.email ?? initialEmailValues : null)
                                    }
                                />

                                <Switch.Label className="ml-3 font-bold">Email</Switch.Label>
                            </Switch.Group>

                            {values.email && (
                                <>
                                    <FormField name="email.subject" label="Subject">
                                        <InputField />
                                    </FormField>

                                    <FormField name="email.body" label="Content">
                                        <RichEditor
                                            key={templateId ?? "new-email"}
                                            showVariableType={false}
                                            initialContent={emailBody}
                                            variables={variableItems.filter((variable) =>
                                                EmailVariables.has(variable.name as Variables),
                                            )}
                                            onChange={(html) => setFieldValue("email.body", html)}
                                        />
                                    </FormField>

                                    <div>
                                        <SecondaryButton
                                            size="small"
                                            icon={<EyeIcon />}
                                            onClick={async () => {
                                                previewTemplate(values.email ?? initialEmailValues);
                                                setPreviewType(CommunicationChannelType.EMAIL);
                                            }}
                                        >
                                            Preview
                                        </SecondaryButton>
                                    </div>
                                </>
                            )}

                            {enabledSellerPreferenceSms ? (
                                <Switch.Group>
                                    <Switch
                                        isChecked={!!values.sms}
                                        size="large"
                                        onChange={(enabled: boolean) => {
                                            setFieldValue("sms", enabled ? template?.sms ?? initialSmsValues : null);
                                        }}
                                    />

                                    <Switch.Label className="ml-3 font-bold">Text Message</Switch.Label>
                                </Switch.Group>
                            ) : null}

                            {enabledSellerPreferenceSms && values.sms && (
                                <>
                                    <RichEditor
                                        key={templateId ?? "new-sms"}
                                        isSmsEditor
                                        showVariableType={false}
                                        initialContent={smsBody}
                                        onChange={(html) => setFieldValue("sms.body", html)}
                                    />
                                    <div className="flex justify-between">
                                        <SecondaryButton
                                            size="small"
                                            icon={<EyeIcon />}
                                            onClick={async () => {
                                                previewTemplate(values.sms ?? initialSmsValues);
                                                setPreviewType(CommunicationChannelType.SMS);
                                            }}
                                        >
                                            Preview
                                        </SecondaryButton>
                                        <div className="flex justify-end text-sm">
                                            <div
                                                className={clsx(
                                                    values.sms.body?.length > 160 ? "text-danger" : "text-gray",
                                                )}
                                            >
                                                {values.sms.body?.length}/160
                                            </div>
                                            <Tooltip content="SMS messages that exceeded 160 characters will be sent as multiple texts. Maximum number of characters is 737">
                                                <CircleInfoIcon />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* This is just workaround solution for footer, should be handled in ui-kit */}
                            <ContentLayout.Footer className={clsx(template && "sticky !-ml-10 !w-[600px]")}>
                                <SubmitButton isLoading={isSaving} type="button" onClick={handleSubmit}>
                                    Save
                                </SubmitButton>

                                <SecondaryButton type="reset" onClick={handleResetClick(handleReset)}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </fieldset>
                    </ContentLayout>
                )}
            </XolaFormik>

            <PreviewMessageTemplateDrawer
                classNames={
                    previewType === CommunicationChannelType.SMS
                        ? { dialogContent: "w-100", children: "!pl-10 pt-20", content: "!mt-0" }
                        : undefined
                }
                isOpen={visible}
                onClose={() => toggle()}
            >
                {templatePreview ? (
                    <iframe sandbox="allow-same-origin" srcDoc={templatePreview} className="h-full w-full" />
                ) : null}
            </PreviewMessageTemplateDrawer>
        </>
    );
};

const templateToInitialValues = (template: MessageTemplate) => ({
    ...template,
    email: template.email?.body ? template.email : undefined,
    sms: template.sms?.body ? template.sms : undefined,
});
